import {
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  EyeOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SummaryLayout from '../../components/layout/SummaryLayout';
import MonthSelector from '../../components/ui/MonthSelector';
import { getData } from '../../config/api-connect';
import { useMonthFilter } from '../../hooks/useMonthFilter';
import { getColumnFilters, getColumnSearchProps } from '../../utils/filters';
import { formatCurrency, renderWithEllipsis, uniqueValues } from '../../utils/helper';
import './gstInvoices.css';

const { Option } = Select;

function GstInvoices() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const passedVenueId = location.state?.venue_id;
  const isKitFee = location.state?.is_kit_fee;
  const passedCityId = location.state?.city_id;

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    getData('/finance/get_cities_and_venues')
      .then((response) => {
        const allCities = response.cities || [];
        const allVenues = response.venues || [];
        setCities(allCities);
        setVenues(allVenues);

        if (isKitFee) {
          const filtered = allVenues.filter((item) => item.city === passedCityId);

          setVenuelist(filtered);
          setSelectedCityId(passedCityId);
          setSelectedVenueId('');
        } else if (passedVenueId) {
          const venueObj = allVenues.find((v) => v.id === passedVenueId);
          if (venueObj) {
            const filtered = allVenues.filter((item) => item.city === venueObj.city);
            setVenuelist(filtered);
            setSelectedCityId(venueObj.city);
            setSelectedVenueId(passedVenueId);
          }
        } else {
          setVenuelist(allVenues);
          setSelectedCityId('');
          setSelectedVenueId('');
        }
        setInitialized(true);
      })
      .catch((error) => console.error('Error:', error.message));
  }, [isKitFee, passedCityId, passedVenueId]);

  useEffect(() => {
    if (selectedCityId) {
      setVenuelist(venues.filter((item) => item.city === selectedCityId));
      const currentVenue = venues.find((v) => v.id === selectedVenueId);
      if (currentVenue && currentVenue.city !== selectedCityId) {
        setSelectedVenueId('');
      }
    } else {
      setVenuelist(venues);
    }
  }, [selectedCityId, selectedVenueId, venues]);

  const getGstInvoices = useCallback(() => {
    setLoading(true);

    const month = selectedMonth.format('MM');
    const year = selectedMonth.format('YYYY');
    const cityQuery = selectedCityId ? `&city_id=${selectedCityId}` : '';
    const venueQuery = selectedVenueId ? `&venue_id=${selectedVenueId}` : '';
    const kitFeeQuery = isKitFee ? `&is_kit_fee=true` : '';
    const typeQuery = `&type=B2C`;

    getData(
      `/finance/get_gst_invoices/?month=${month}&year=${year}${cityQuery}${venueQuery}${kitFeeQuery}${typeQuery}`,
    )
      .then((response) => {
        setInvoices(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error fetching invoices:', error.message);
      });
  }, [selectedCityId, selectedVenueId, selectedMonth, isKitFee]);

  useEffect(() => {
    if (!initialized) return;
    getGstInvoices();
  }, [initialized, selectedCityId, selectedVenueId, selectedMonth, getGstInvoices]);

  const columns = [
    {
      title: 'Verified',
      dataIndex: 'is_verified',
      align: 'center',
      fixed: 'right',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (isVerified) => (
        <Tooltip title={isVerified ? 'Collection verified' : 'Collection not verified'}>
          {isVerified ? (
            <CheckCircleOutlined className="gst-invoices-check-icon" />
          ) : (
            <CloseCircleOutlined className="gst-invoices-close-icon" />
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      align: 'left',
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnSearchProps('customer'),
      onFilter: (value, record) => {
        const name = record?.customer ? record.customer.toString().toLowerCase() : '';
        const mobile = record?.mobile_no ? record.mobile_no.toString().toLowerCase() : '';
        return name.includes(value.toLowerCase()) || mobile.includes(value.toLowerCase());
      },
      render: (text, record) => (
        <>
          <div>{text ?? <span className="gst-invoices-text-muted">—</span>}</div>
          <div className="gst-invoices-subtext">{record?.mobile_no ?? ''}</div>
        </>
      ),
    },
    {
      title: 'Sales Model',
      dataIndex: 'sales_model',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnFilters('sales_model', uniqueValues(invoices, 'sales_model')),
    },
    {
      title: 'Slab',
      dataIndex: 'slab',
      align: 'center',
      ...getColumnFilters('slab', uniqueValues(invoices, 'slab')),
    },
    {
      title: 'Taxable Value',
      dataIndex: 'taxable_value',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.taxable_value) || 0) - (Number(b.taxable_value) || 0),
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'CGST',
      dataIndex: 'cgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'SGST',
      dataIndex: 'sgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'Total (incl. GST)',
      dataIndex: 'total',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      render: (val) =>
        val == null ? (
          <span className="gst-invoices-text-muted">—</span>
        ) : (
          <strong>{formatCurrency(val)}</strong>
        ),
    },
    {
      title: 'Venue',
      dataIndex: 'venue',
      align: 'center',
      width: 250,
      render: (val) => renderWithEllipsis(val?.name, { width: 200, lines: 2 }),
      ...getColumnFilters('venue.name', uniqueValues(invoices, 'venue', 'name')),
    },
    {
      title: 'Package',
      dataIndex: 'package',
      align: 'left',
      render: (val) => renderWithEllipsis(val, { width: 200, lines: 2 }),
      ...getColumnSearchProps('package'),
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnSearchProps('invoice_number'),
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: '',
      dataIndex: 'pdf',
      align: 'center',
      fixed: 'right',
      render: (pdf) => {
        return (
          <Tooltip title={pdf ? 'View Invoice PDF' : 'Invoice PDF not available'}>
            <EyeOutlined
              className="gst-invoices-pdf-icon"
              data-available={pdf ? 'true' : 'false'}
              onClick={(e) => {
                if (!pdf) return;
                e.stopPropagation();
                window.open(pdf, '_blank', 'noopener,noreferrer');
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const pageTitle = (
    <div className="gst-invoices-page-title">
      <ArrowLeftOutlined className="gst-invoices-back-icon" onClick={() => navigate(-1)} />
      <span>GST Invoices - Full Ops</span>
    </div>
  );

  const chooseCity = (value) => {
    setSelectedCityId(value);
    setSelectedVenueId('');
    localStorage.setItem('selectedCity', JSON.stringify(value));
  };

  const renderRightSideActions = () => (
    <>
      <Select
        showSearch
        placeholder="Select City"
        className="gst-invoices-select-city"
        value={selectedCityId}
        onChange={(value) => chooseCity(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {cities.map((city) => (
          <Option key={city.id} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>

      <Select
        showSearch
        placeholder="Select Venue"
        className="gst-invoices-select-venue"
        value={selectedVenueId}
        onChange={(value) => setSelectedVenueId(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="">All Venues</Option>
        {venuelist.map((venue) => (
          <Option key={venue.id} value={venue.id}>
            {venue.name}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
    </>
  );

  const totalTaxable = invoices.reduce((acc, curr) => acc + (Number(curr.taxable_value) || 0), 0);
  const totalGst = invoices.reduce(
    (acc, curr) => acc + (Number(curr.cgst) || 0) + (Number(curr.sgst) || 0),
    0,
  );
  const totalAmount = invoices.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);

  const statsConfig = [
    {
      key: 's_invoices',
      title: 'Total Invoices',
      icon: <FileTextOutlined className="gst-invoices-stat-icon gst-invoices-stat-blue" />,
    },
    {
      key: 's_taxable',
      title: 'Total Taxable Value',
      icon: <DatabaseOutlined className="gst-invoices-stat-icon gst-invoices-stat-darkblue" />,
    },
    {
      key: 's_gst',
      title: 'Total GST Collected',
      icon: (
        <SafetyCertificateOutlined className="gst-invoices-stat-icon gst-invoices-stat-yellow" />
      ),
    },
    {
      key: 's_total',
      title: 'Total Amount',
      icon: <BankOutlined className="gst-invoices-stat-icon gst-invoices-stat-green" />,
    },
  ];

  const totals = {
    s_invoices: invoices.length,
    s_taxable: `₹ ${totalTaxable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_gst: `₹ ${totalGst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_total: `₹ ${totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
  };

  return (
    <>
      <SummaryLayout
        title={pageTitle}
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: 'id',
          columns,
          data: invoices,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
          onRow: (record) => ({
            style: { cursor: 'pointer' },
          }),
        }}
      />
    </>
  );
}

export default GstInvoices;
